import {IGeneralSelectedPortfolios, IGeneralSelection} from '../../models/generalSelection';
import * as ClientActions from '../../store/client/actions/client.actions';
import { IGeneralSelectionState } from '../../store/state/general-selection.state';
import { map } from 'rxjs/operators';
import { NameValueMapper } from 'src/app/services/mapper/name-value-mapper.';
import * as GeneralSelectionActions from '../../store/general-selection/actions/general-selection.actions';
import { TokenService } from '../token/token.service';
import { Router, ActivatedRoute, Data } from '@angular/router';
import { FeatureToggleService } from '../feature-toggle/feature-toggle.service';
import { IAppState } from '../../store/state/app.state';
import { Store } from '@ngrx/store';
import { INameValueFilter } from 'src/app/models/filter';
import { GeneralSelectionFacade } from 'src/app/store/general-selection/facade/general-selection.facade';
import { areArraysUnequal } from '../../utils/comparative.methods';
import { IClient, IEntity } from '../../store/client/reducers/client.reducers';
import * as fromClient from 'src/app/store/client/reducers/client.reducers';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { ClientFacade } from 'src/app/store/client/facade/client.facade';
import { ClientEntity } from '../../models/clients';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { Injectable } from "@angular/core";
import { Features } from 'src/app/models/features';
import { CurrencyService } from '../utils/currency.service';
import {IPortfolio} from "../../models/portfolios";
import { PortfoliosFacade } from 'src/app/store/portfolios/facade/portfolios.facade';
import { EntitySubType } from 'src/app/models/entityTypes';
import { SiteIds } from 'src/app/models/sites';
import { AppConfig } from 'src/app/app.config';
import { PerformanceFacade } from 'src/app/store/performance/facade/performance.facade';
@UntilDestroy()
@Injectable()
export class ClientEntitySelectService {

    private selectedClient: IClient;
    private clientList: IClient[];
    private defaultClientList$: Observable<IClient[]> = this.clientFacade.defaultClientList$;
    private clientList$: Observable<IClient[]> = this.clientFacade.clientList$;
    private selectedClientGroup: IClient;
    private currentUrl: string;
    private isDataInitalised = false;
    private isAdministrator = this.tokenService.isAdministrator();

    private allClientEntities: ClientEntity[] = [];
    private allClientEntitiesSubject: BehaviorSubject<ClientEntity[]> = new BehaviorSubject([]);
    public allClientEntities$: Observable<ClientEntity[]> = this.allClientEntitiesSubject.asObservable();

    private nameKey = 'GroupName';
    private groupIdKey = 'GroupId';
    private entityIdKey = 'EntityId';

    private selectedEntitiesSubject: BehaviorSubject<ClientEntity[]> = new BehaviorSubject(null);
    public selectedEntities$: Observable<ClientEntity[]> = this.selectedEntitiesSubject.asObservable();
    private hasMultipleEntitiesSubject = new BehaviorSubject(false);
    public hasMultipleEntities$: Observable<boolean> = this.hasMultipleEntitiesSubject.asObservable();

    private selectedEntities: ClientEntity[] = [];
    private selectedDate: Date;

    public defaultClientSearchList$: Observable<INameValueFilter[]> = this.defaultClientList$.pipe(map((cl: IClient[]) => {
        this.clientList = cl;
        if (!this.clientList.length) {
            return [];
        }

        return this.mapClientsToNameValueFilters(cl);
    }));

    public clientSearchList$: Observable<INameValueFilter[]> = this.clientList$.pipe(map((cl: IClient[]) => {
        this.clientList = cl;
        if (!this.clientList.length) {
            return [];
        }

        return this.mapClientsToNameValueFilters(cl);
    }));

    constructor(
        private store: Store<IAppState>,
        private clientFacade: ClientFacade,
        private route: ActivatedRoute,
        private tokenService: TokenService,
        private featureToggleService: FeatureToggleService,
        private router: Router,
        private nameValueMapper: NameValueMapper,
        private generalSelectionFacade: GeneralSelectionFacade,
        private portfolioFacade: PortfoliosFacade,
        private currencyService: CurrencyService,
        private appConfig: AppConfig,
        private performanceFacade: PerformanceFacade
    ) {
        this.setStateSubscriptions();
    }

    public updateSelectedEntitiesSubject(entities: ClientEntity[]) {
        this.selectedEntitiesSubject.next([...entities]);
    }

    public updateAllClientEntitiesSubject(entities: ClientEntity[]) {
        this.allClientEntitiesSubject.next([...entities]);
    }

    /**
     * Updates the general selection state with the selected entities/portfolios/clients and date.
     */
    public updateGeneralSelection(date: Date, selectedClient?: fromClient.IClient, selectedEntities?: ClientEntity[], 
                                  selectedPortfolios?: IPortfolio[], selectedCurrency?: string) {
        let selectedEntitiesUpdated = areArraysUnequal(selectedEntities ?? [], this.selectedEntities, 'EntityId');
        
        this.selectedEntities = selectedEntities ? selectedEntities : this.selectedEntities;
        this.selectedClient = selectedClient ? selectedClient : this.selectedClient;

        const updateSelectionParams: IGeneralSelection = {
            toDate: date,
            
            selectedEntities: this.selectedEntities,
            selectedPortfolios: selectedPortfolios,
            selectedClient: this.selectedClient
        };

        this.reloadConfig(selectedEntitiesUpdated);

        this.store.dispatch(new GeneralSelectionActions.UpdateSelection(updateSelectionParams));
        
        const currency = this.selectedEntities.length > 0 && this.selectedEntities[0].NetAssetValue !== undefined
            ? this.selectedEntities[0].NetAssetValue.CurrencyCode : selectedCurrency;
        
        if(currency)
            this.updateCurrency(currency);   
        
    }

    /**
     * Reloads the app config and navigates the user back to the dashboard.
     * This is currently only implemented for the Investnow Combined Portal AND when selected entities have changed
     * to try and minimise disruption to other portals. 
     */
    private reloadConfig(selectedEntitiesUpdated: boolean = false): void {
        if(this.appConfig.appConfigSignal().env.siteId === SiteIds.INVESTNOWCOMBINED && selectedEntitiesUpdated){
            let configName = this.selectedEntities[0].EntitySubType == EntitySubType.KiwiSaverInvestor ? 'investnow-kiwisaver' : 'investnowv2';

            this.appConfig.loadConfig(null,configName,false)
            .then( () => {
                this.router.navigate(['/feature','client','dashboard']);
            })
        }
    }

    private updateCurrency(selectedCurrency: string): void {
        this.currencyService.updateSelectedCurrencyCheck(selectedCurrency);
    }

    public updateGeneralSelectionPortfolios(date: Date, selectedPortfolios: IPortfolio[]){
        const actionPayload : IGeneralSelectedPortfolios = {
          toDate: date,
          selectedPortfolios: selectedPortfolios
        };

        this.store.dispatch(new GeneralSelectionActions.UpdateSelectedPortfolios(actionPayload));
    }

    public setGroups(date: Date, selectedClient?: fromClient.IClient) {
        this.selectedClient = selectedClient ? selectedClient : this.selectedClient;
        const updateGroups: {
            selectedClient: IClient;
            date: Date;
        } = {
            selectedClient: this.selectedClient,
            date: date
        };

        this.store.dispatch(new ClientActions.UpdateEntityDateData(updateGroups));
    }

    public setMultipleInvestorEntities(date: Date){
        this.store.dispatch(new ClientActions.GetClientInvestorEntities(date));
    }

    public searchClient(query: string) {

        var clientSearchModel = {
            query: query,
            entitySubType: this.getEntitySubTypeForSearch()
        } as fromClient.IClientSearchModel;

        this.store.dispatch(new ClientActions.GetClientGroups(clientSearchModel));
    }

    // Get client by given client Id.
    // Client Id can be either GroupId or EntityId.
    public selectClient(selectedClientId: number) {
        // Try to find client by GroupId
        let selectedClient = this.clientList.find(option => option[this.groupIdKey] === selectedClientId);
        if (!selectedClient) {
          // Try to find client by EntityId
          selectedClient = this.clientList.find(option => option[this.entityIdKey] === selectedClientId);
        }
        this.clearPerformanceState();
        this.retrieveClient(selectedClient, this.selectedDate);
    }

    // TODO: not sure if we still need it  
    public updateEntityDateData(selectedDate: Date) {
        const clientPayload = {
            selectedClient: {
                ...this.selectedClient,
            },
            date: selectedDate
        };
        if (this.clientFacade.selectedDate !== selectedDate) {
            if (this.clientFacade.canMultiSelect) {
                this.store.dispatch(new ClientActions.UpdateEntityDateData(clientPayload));
            } else {
                this.store.dispatch(new GeneralSelectionActions.UpdateDate(selectedDate));
            }
        }
    }

    /**
     * Clears/resets performance state when switching between clients.
     */
    private clearPerformanceState() : void {
        this.performanceFacade.clearPerformanceState();
    }

    /**
     * Sets up the required state subscriptions.
     */
    private setStateSubscriptions() {
        this.subscribeToSelectedClientGroupAndSelectedDate();
        this.subscribeToGeneralSelectionState();
        this.subscribeToRoute();
        this.subscribeToSelectedClientGroup();
    }

    private subscribeToSelectedClientGroupAndSelectedDate(): void{
        combineLatest([
            this.clientFacade.selectedClientGroup$,
            this.generalSelectionFacade.getSelectedDate()
        ]).pipe(untilDestroyed(this)).subscribe((v: [fromClient.IClient, Date]) => {
            this.selectedClientGroup = v[0];
            this.selectedDate = v[1];
            if (this.selectedClientGroup.Members && !!this.selectedDate && areArraysUnequal(this.allClientEntities, this.selectedClientGroup.Members, 'EntityId')) {
                this.allClientEntities = this.selectedClientGroup.Members.map((item) => {
                    return {
                        ...item
                    };
                });
                this.hasMultipleEntitiesSubject.next(this.allClientEntities.length > 1);
                this.setupEntityState();
            }
        });
    }

    private subscribeToGeneralSelectionState(): void {
        this.generalSelectionFacade.getState()
        .pipe(untilDestroyed(this))
        .subscribe((state: IGeneralSelectionState) => {
            this.currentUrl = state.baseUrl;
            this.isDataInitalised = state.dataInitalised;
            this.selectedClient = state.selectedClient;

            if (areArraysUnequal(state.selectedEntities, this.selectedEntities, 'EntityId')) {
                this.selectedEntities = [...state.selectedEntities];
                this.allClientEntities.forEach(entity => this.mapEntitySelection(entity));
                this.updateAllClientEntitiesSubject([...this.allClientEntities]);
                this.updateSelectedEntitiesSubject([...this.selectedEntities]);
            }
        });
    }

    private subscribeToRoute(){
        this.route.data.subscribe((data: Data) => {
            this.currentUrl = data['currentUrl'];
        });
    }

    /**
     * Subscribes to the selected client group from the general selection state.
     */
    private subscribeToSelectedClientGroup(){
        this.clientFacade.selectedClientGroup$
        .pipe(untilDestroyed(this))
        .subscribe((selectedClientGroup: IClient) => {
            this.selectedClientGroup = selectedClientGroup;
            this.resolveIfClientDataIsInitialised(selectedClientGroup);
        });
    }

    /**
     * Determines if client data has been intiialised (on initial load?) and if so dispatches
     * an action to set data initialised to true in the general selection state
     */
    private resolveIfClientDataIsInitialised(selectedClientGroup: IClient): void {
        if (this.isAdministrator) {
            if (selectedClientGroup.GroupId !== 0 && !this.isDataInitalised) {
                this.currentUrl = (this.currentUrl === '') ? 'feature/client/dashboard' : this.currentUrl;
                this.store.dispatch(new GeneralSelectionActions.SetClientDataInitialised());
            }
        }
    }

    /**
     * Determines if the passed in entity is currently selected based on whether it appears in the
     * selectedEntities list. This is a way to keep which entities are selected synced across the
     * selectedEntities and allClientEntities.
     */
    private mapEntitySelection(entity: ClientEntity) {
        const match = this.selectedEntities.find(selectedEntity => selectedEntity.EntityId === entity.EntityId);
        const isChecked = (match) ? true : false;
        entity = {
            ...entity,
            Checked: isChecked
        };
        return entity;
    }

    /**
     * Primary purpose of this method seems to be keeping the allClientEntities and selectedEntities lists
     * in sync
     */
    private setupEntityState() {
        if (!this.selectedClientGroup || !this.selectedClientGroup.Members) {
            return;
        }

        if (!this.clientFacade.isEntityDateDataUpdated) {
            const currency = this.allClientEntities[0]?.NetAssetValue.Currency;
            let selectedCount = 0;
            
            this.allClientEntities = this.allClientEntities.map((entity) => {
                const checked = (this.clientFacade.canMultiSelect || selectedCount < 1) && entity.NetAssetValue.Currency === currency;
                if (checked)
                    selectedCount++;

                return {
                    ...entity,
                    Checked: checked
                };
            });
        } else {
            let selectedCount = 0;

            //TODO - I believe in some scenarios allClientEntities and selectedEntities have completely
            //fallen out of sync which causes issues when resolving the selectedEntities
            this.allClientEntities = this.allClientEntities.map((entity) => {
                let similar = false;
                this.selectedEntities.forEach((selectedEntity) => {
                    if (selectedEntity.EntityId === entity.EntityId) {
                        similar = true;
                    }
                });

                const checked = (this.clientFacade.canMultiSelect || selectedCount < 1) && similar;
                if (checked)
                    selectedCount++;

                return {
                    ...entity,
                    Checked: checked
                };
            });
        }

        if (this.allClientEntities.length > 0) {
            //If we can't multiselect pick the first entity in the list and set it as the selected entity.
            if (!this.clientFacade.canMultiSelect) {
                this.allClientEntities[0].Checked = true;
                this.selectedEntities = [this.allClientEntities[0]];
            } else {
                //This is a patch to fix a bug when switching between group entities (such as we have for WB or Appolinaris)
                //and non group entities. I think part of the problem was a race condition but for some reason the selected entity
                //was not being reflected correctly which meant the application data would not refresh when changing entities.
                //The solution is if we only have one entity in allClientEntities it is the selected entity by default.
                if(this.allClientEntities.length === 1){
                    this.allClientEntities[0].Checked = true;
                    this.selectedEntities = [this.allClientEntities[0]];
                } 
                //Otherwise we determine the selected entities from the allClientEntities list where 'checked' is set to true.
                else {
                    this.selectedEntities = this.allClientEntities.filter((entity) => typeof entity.Checked !== 'undefined' && entity.Checked === true);
                }
            }

            this.updateSelectedEntitiesSubject([...this.selectedEntities]);
        }

        if (!this.clientFacade.needClientBeSelected) {
            if (this.selectedClientGroup.Members.length > 0) {
                this.updateGeneralSelection(this.selectedDate);
            }
        }

        // needed for now to update header component.
        this.updateAllClientEntitiesSubject(this.allClientEntities);
    }

    private retrieveClient(selectedClient: IClient, date: Date) {
        // Select either a client or a singly entity from the search results
        const clientPayload: {
            selectedClient: fromClient.IClient;
            date: Date;
        } = {
            selectedClient,
            date
        };

        if (selectedClient.GroupId) {
            if (selectedClient.GroupId !== this.selectedClientGroup.GroupId) {
                this.store.dispatch(new ClientActions.SetSelectedClientGroup(clientPayload));
                this.updateGeneralSelection(date, selectedClient);

                // Load all the investors' portfolios for group.
                const entityIds = selectedClient.Members.map((member) => member.EntityId);
                this.portfolioFacade.loadPortfoliosForEntities(entityIds);
            }
        } else {
            //This client is not a group it is an individual investor so it does not have any members
            //Set the member array to empty
            selectedClient = {
                ...selectedClient,
                Members: []
            };

            this.store.dispatch(new ClientActions.SetSelectedSingleEntityClient(clientPayload));

            let selectedEntity = selectedClient as any;
            //There can only be one investor selected so checked is set to true by default
            selectedEntity = {
                ...selectedEntity,
                Checked: true
            };

            this.allClientEntities = [];
            this.allClientEntities.push(selectedEntity);

            // needed for now to update header component.
            // TODO, entity selection needs to be refactored more
            this.updateAllClientEntitiesSubject(this.allClientEntities);
            this.updateGeneralSelection(date, selectedClient);

            // Load portfolios.
            this.portfolioFacade.loadPortfoliosForEntities([selectedClient.EntityId]);
        }

        this.router.navigate(['feature/client/dashboard', { outlets: { sidebar: null } }]);
    }

    private mapClientsToNameValueFilters(clients: IClient[]) : INameValueFilter[] {
      // Filter client list with groupIds
      let clientListWithGroupIds = clients.filter(client => client.GroupId || client.GroupId === 0);
      let mappedClientListWithGroupIds = <INameValueFilter[]>this.nameValueMapper.map(clientListWithGroupIds, this.nameKey, this.groupIdKey);

      // Filter client list without groupIds - Map to use EntityId as value
      let clientListWithoutGroupIds = clients.filter(client => !client.GroupId);
      let mappedClientListWithoutGroupIds = <INameValueFilter[]>this.nameValueMapper.map(clientListWithoutGroupIds, this.nameKey, this.entityIdKey);

      return mappedClientListWithGroupIds.concat(mappedClientListWithoutGroupIds);
    }

    /**
     * Resolves the entity sub types that should be used when requesting clients.
     */
    private getEntitySubTypeForSearch(): number | null{
        const siteId = this.appConfig.getEnvironment().siteId;

        if(siteId && (siteId == SiteIds.INVESTNOW || siteId === SiteIds.EVIDENTIAL)){
            return EntitySubType.KiwiSaverInvestor
        }

        return null;
    }
}
